import { useCallback, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ExternalLink, Loader2, RefreshCw } from 'lucide-react';
import type { GraphData, GraphNode } from '@/types/graph';
import { Button } from '@/components/Common/button';
import { Graph2D } from '@/components/OntologyGraph/Graph2D';
import { TypeFilterDropdown } from '@/components/OntologyGraph/TypeFilterDropdown';
import { chipClass } from '@/components/OntologyGraph/graphStyles';
import { colorFor, filterNodes } from '@/components/OntologyGraph/graphUtils';
import type { LegendGroup, VizGraph } from '@/components/OntologyGraph/types';

// Same palette and legend keys as the Memory graph (KnowledgeGraphPage).
const STRUCTURE_TYPES = ['Domain', 'UseCase', 'Cadrage', 'Document', 'Theme', 'SourceType'] as const;
const STRUCTURE_SET: ReadonlySet<string> = new Set(STRUCTURE_TYPES);
const DOCUMENT_TYPES: ReadonlySet<string> = new Set(['Domain', 'UseCase', 'Cadrage', 'Document']);
const TYPE_COLOR_OVERRIDES: Record<string, string> = { Domain: '#1D4ED8', SourceType: '#EF4444' };
const UNCLASSIFIED = '__unclassified__';

function typeKey(node: GraphNode): string {
  if (node.type !== 'Entity') return node.type;
  return node.entity_type?.trim() || UNCLASSIFIED;
}

/** Link to the page of a use case or solution node, if the node is one. */
function contextLink(nodeId: string): string | null {
  if (nodeId.startsWith('usecase_')) return `/cas-usage/${nodeId.slice('usecase_'.length)}`;
  if (nodeId.startsWith('cadrage_')) return `/cadrages/${nodeId.slice('cadrage_'.length)}`;
  return null;
}

interface Props {
  /** Domain-scoped context graph, loaded by the page (see useDomainContextGraph). */
  graph: GraphData | null;
  loading: boolean;
  error: string | null;
  onReload: () => void;
}

/** Graph of a domain's indexed documents and the use cases / solutions they belong to. */
export function DomainDocumentsGraph({ graph: raw, loading, error, onReload }: Props) {
  const { t } = useTranslation();
  const [showKnowledge, setShowKnowledge] = useState(false);
  // Only consulted when knowledge is shown; the document view is fixed.
  const [hidden, setHidden] = useState<Set<string>>(() => new Set(['SourceType']));
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const typeLabel = useCallback(
    (type: string) => {
      if (type === UNCLASSIFIED) return t('knowledgeGraph.types.unclassified');
      if (STRUCTURE_SET.has(type)) return t(`knowledgeGraph.types.${type}`);
      return type;
    },
    [t],
  );

  const colorOf = useCallback(
    (type: string, fallback?: string) =>
      STRUCTURE_SET.has(type) ? TYPE_COLOR_OVERRIDES[type] ?? fallback ?? '#6B7280' : colorFor(type),
    [],
  );

  const keyById = useMemo(() => new Map((raw?.nodes ?? []).map((n) => [n.id, typeKey(n)])), [raw]);

  const full = useMemo<VizGraph>(() => {
    if (!raw) return { nodes: [], links: [] };
    return {
      nodes: raw.nodes.map((n) => {
        const type = typeKey(n);
        return { id: n.id, label: n.label, type: typeLabel(type), color: colorOf(type, n.color) };
      }),
      links: raw.edges.map((e) => ({ source: e.source, target: e.target, label: e.type })),
    };
  }, [raw, typeLabel, colorOf]);

  const legendGroups = useMemo<LegendGroup[]>(() => {
    const counts = new Map<string, number>();
    keyById.forEach((k) => counts.set(k, (counts.get(k) ?? 0) + 1));
    const entry = (type: string) => ({
      type,
      label: typeLabel(type),
      color: colorOf(type, raw?.nodes.find((n) => n.type === type)?.color),
      count: counts.get(type) ?? 0,
    });
    const classes = [...counts.keys()].filter((k) => !STRUCTURE_SET.has(k)).sort((a, b) => a.localeCompare(b));
    return [
      { title: t('knowledgeGraph.legend.structure'), types: STRUCTURE_TYPES.filter((s) => counts.has(s)).map(entry) },
      { title: t('knowledgeGraph.legend.classes'), types: classes.map(entry) },
    ];
  }, [keyById, raw, t, typeLabel, colorOf]);

  const data = useMemo<VizGraph>(() => {
    const visible = (id: string) => {
      const key = keyById.get(id) ?? '';
      return showKnowledge ? !hidden.has(key) : DOCUMENT_TYPES.has(key);
    };
    return filterNodes(full, visible);
  }, [full, keyById, hidden, showKnowledge]);

  const toggleType = (type: string) =>
    setHidden((prev) => {
      const next = new Set(prev);
      if (next.has(type)) next.delete(type);
      else next.add(type);
      return next;
    });

  const selected = selectedId ? data.nodes.find((n) => n.id === selectedId) : undefined;
  const selectedLink = selected ? contextLink(selected.id) : null;
  const documentCount = raw?.statistics.documents ?? 0;

  return (
    <section className="overflow-hidden rounded-lg border border-border">
      <div className="flex flex-wrap items-center gap-3 border-b border-border px-4 py-3">
        <div className="min-w-0 flex-1">
          <h3 className="text-sm font-medium text-foreground">{t('domainDetail.graph.title')}</h3>
          <p className="text-xs text-muted-foreground">{t('domainDetail.graph.description')}</p>
        </div>
        <button
          type="button"
          onClick={() => setShowKnowledge((v) => !v)}
          className={chipClass(showKnowledge)}
          disabled={documentCount === 0}
        >
          {t('domainDetail.graph.showKnowledge')}
        </button>
        {showKnowledge && (
          <TypeFilterDropdown
            label={t('knowledgeGraph.legend.title')}
            groups={legendGroups}
            hidden={hidden}
            onToggle={toggleType}
            onShowAll={() => setHidden(new Set())}
          />
        )}
        <Link
          to="/knowledge-base/graph"
          className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
        >
          {t('domainDetail.graph.openInMemory')}
          <ExternalLink className="h-3 w-3" />
        </Link>
        <Button
          variant="ghost"
          className="h-8 w-8 p-0"
          onClick={onReload}
          disabled={loading}
          title={t('domainDetail.graph.refresh')}
          aria-label={t('domainDetail.graph.refresh')}
        >
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
        </Button>
      </div>

      <div className="relative h-[65vh] min-h-[480px]">
        {loading && !raw && (
          <div className="flex h-full items-center justify-center">
            <Loader2 className="h-6 w-6 animate-spin text-primary" />
          </div>
        )}
        {error && (
          <div className="flex h-full flex-col items-center justify-center gap-3 text-center">
            <p className="text-sm font-semibold text-destructive">{t('domainDetail.graph.loadError')}</p>
            <p className="text-xs text-muted-foreground">{error}</p>
            <Button size="sm" onClick={onReload}>
              {t('domainDetail.graph.retry')}
            </Button>
          </div>
        )}
        {!error && raw && documentCount === 0 && (
          <div className="flex h-full items-center justify-center px-6 text-center text-sm text-muted-foreground">
            {t('domainDetail.graph.empty')}
          </div>
        )}
        {!error && raw && documentCount > 0 && (
          <Graph2D
            data={data}
            onSelect={setSelectedId}
            selectedId={selectedId}
            onBackgroundClick={() => setSelectedId(null)}
          />
        )}
        {selected && (
          <div className="absolute inset-x-3 bottom-3 z-10 flex items-center gap-2 rounded-md border border-border bg-card/95 px-3 py-2 text-xs shadow-sm">
            <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ background: selected.color }} />
            <span className="min-w-0 flex-1 truncate font-medium">{selected.label}</span>
            <span className="shrink-0 text-muted-foreground">{selected.type}</span>
            {selectedLink && (
              <Link to={selectedLink} className="shrink-0 font-medium text-primary hover:underline">
                {t('domainDetail.graph.open')}
              </Link>
            )}
          </div>
        )}
      </div>
    </section>
  );
}

export default DomainDocumentsGraph;
